import store from '@app/store';
import { toast } from '@components/Toast';
import env from '@config/env';
import { logout, setTokens } from '@redux/auth/authSlice';
import { startLoading, stopLoading } from '@redux/loading/loadingSlice';
import { navigateToLogin } from '@services/appNavigation';
import {
  redirectMerchantOnForbidden,
  shouldHandleMerchantForbiddenRedirect,
} from '@services/merchantNavigation';
import encryptedStorage from '@storage/encryptedStorage';
import { STORAGE_KEYS } from '@storage/storageKeys';
import { getApiErrorMessage } from '@utils/apiError';
import {
  applyAuthorizationHeader,
  extractAuthTokens,
  getAccessToken,
  getUserIdFromToken,
  getRefreshToken,
  getRefreshTokenPath,
  isAccessTokenExpired,
  setAuthorizationHeader,
  shouldSkipTokenRefresh,
  unwrapAuthData,
} from '@utils/authTokens';
import { getMerchantDeviceId, getOsLabel } from '@utils/deviceHeaders';
import { decrypt, encrypt } from '@utils/encryptDecryptUtils';
import { assertValidRequestUrl, resolveRequestUrl } from '@utils/requestUrl';
import axios from 'axios';

import axiosInstance, { UPLOAD_TIMEOUT_MS } from './axios';

let interceptorsReady = false;
let isRefreshing = false;
let isLoggingOut = false;
let failedQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  store.dispatch(logout());
  encryptedStorage.removeItem(env.REFRESH_TOKEN_KEY);
  navigateToLogin();

  // SPA navigate does not reload; allow a later session to expire again.
  window.setTimeout(() => {
    isLoggingOut = false;
  }, 0);
};

const isRefreshSessionExpired = (refreshError) => {
  const status = refreshError?.response?.status;
  if (env.APP_TYPE === 'admin') return status === 401;
  return status === 401;
};

const logoutAfterFailedRefresh = () => {
  toast.error('Session expired. Please login again.');
  handleLogout();
};

/**
 * UUID used for AES key derivation.
 * Null before login (ENCRYPTION_KEY). After login, userId from verify-OTP storage.
 */
const getStoredEncryptionUserId = () => {
  const storedId = encryptedStorage.getItem(STORAGE_KEYS.ENCRYPTION_USER_ID);
  if (storedId != null && storedId !== '') return String(storedId);

  const verifyResponse = encryptedStorage.getItem(STORAGE_KEYS.VERIFY_OTP_RESPONSE);
  if (!verifyResponse || typeof verifyResponse !== 'object') return null;

  const data = unwrapAuthData(verifyResponse);
  const token = data.accessToken || data.token || null;
  const userId = data.userId || data.id || (token ? getUserIdFromToken(token) : null) || null;

  return userId == null || userId === '' ? null : String(userId);
};

const getEncryptionUserId = () => {
  // Prefer the token actually sent on the request (localStorage), not Redux,
  // so a JWT pasted in Application encrypts with that token's userId.
  const token = getAccessToken() || store.getState()?.auth?.token;
  if (!token) return null;

  const fromToken = getUserIdFromToken(token);
  if (fromToken) return fromToken;

  const fromStorage = getStoredEncryptionUserId();
  if (fromStorage) return fromStorage;

  const user = store.getState()?.auth?.user;
  const fromUser = user?.id || user?.userId || null;
  return fromUser == null || fromUser === '' ? null : String(fromUser);
};

const isFormDataBody = (data) => typeof FormData !== 'undefined' && data instanceof FormData;

const APPLICATION_NAME_BY_PORTAL = {
  merchant: 'MERCHANT_APP',
  admin: 'ADMIN_APP',
  businessuser: 'BUSINESS_USER_APP',
};

/** Headers sent by the Flutter merchant app (device + app identity). */
const applyApiHeaders = (headers = {}) => {
  const next = headers;
  const set = (key, value) => {
    if (value == null || value === '') return;
    if (typeof next.set === 'function') {
      if (!next.get?.(key)) next.set(key, value);
      return;
    }
    if (next[key] == null && next[key.toLowerCase()] == null) {
      next[key] = value;
    }
  };

  set('X-Version', env.VERSION || '1.0.0');
  set('X-Application-Name', APPLICATION_NAME_BY_PORTAL[env.APP_TYPE] || 'MERCHANT_APP');
  set('X-Device-Id', getMerchantDeviceId());
  set('X-Device-Type', 'WEB');
  set('X-OS', getOsLabel());

  return next;
};

const isApiEncryptionEnabled = (config) => env.ENCRYPTION_ENABLE && !config?.skipEncryption;

/** Refresh uses SECRET_KEY only — do not append logged-in userId to AES key material. */
const isRefreshTokenUrl = (url = '') => /\/auth\/refresh\/?$/.test(String(url).split('?')[0]);

const getRequestEncryptionUserId = (config) => {
  if (isRefreshTokenUrl(config?.url)) return null;
  return getEncryptionUserId();
};

const logApiRequest = (config) => {
  const token = config._retryToken || getAccessToken();
  console.log('[API request]', {
    url: config.url,
    method: config.method,
    authorization: token ? `Bearer ${token.slice(0, 16)}…` : null,
    encryptionUserId: getRequestEncryptionUserId(config),
    payload: config._plainPayload ?? config.data,
    encryptedBody: config.data,
  });
};

/**
 * Encrypt JSON request bodies as `{ body: "<Base64 ciphertext>" }`
 * (matches Flutter / Java EncryptDecryptUtils wire format).
 * Opt out with `skipEncryption: true` or `ENCRYPTION_ENABLE=false`.
 * FormData is never encrypted.
 */
const encryptRequestData = (config) => {
  if (!isApiEncryptionEnabled(config) || config.data == null || isFormDataBody(config.data)) {
    return config;
  }

  // Already wrapped (e.g. retry) — leave as-is.
  if (
    typeof config.data === 'object' &&
    !Array.isArray(config.data) &&
    typeof config.data.body === 'string' &&
    Object.keys(config.data).length === 1
  ) {
    return config;
  }

  config._plainPayload = config.data;
  const plain = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
  const encrypted = encrypt(plain, getRequestEncryptionUserId(config));

  if (!encrypted) {
    if (env.IS_DEV) {
      console.error('[API] Failed to encrypt request payload');
    }
    return config;
  }

  config.data = { body: encrypted };
  return config;
};

const parseDecryptedPayload = (cipherText, userId) => {
  const plain = decrypt(cipherText, userId);
  if (plain == null) return null;

  try {
    return JSON.parse(plain);
  } catch {
    return plain;
  }
};

const getResponseCipherText = (payload) => {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object') {
    if (typeof payload.body === 'string') return payload.body;
    // Stage API wraps ciphertext as `{ data: "<Base64>" }`.
    if (typeof payload.data === 'string') return payload.data;
  }
  return null;
};

/**
 * Decrypt API responses wrapped as `{ body }` or `{ data }` Base64 ciphertext.
 * Tries the logged-in userId first, then SECRET_KEY (ENCRYPTION_KEY).
 * Pass `userId` to override (e.g. `null` for refresh-token).
 */
const decryptResponseData = (payload, userId = getEncryptionUserId()) => {
  if (payload == null) return payload;

  const cipherText = getResponseCipherText(payload);
  if (!cipherText) return payload;

  const decrypted =
    parseDecryptedPayload(cipherText, userId) ??
    (userId != null ? parseDecryptedPayload(cipherText, null) : null);
  return decrypted != null ? decrypted : payload;
};

const buildEncryptedBody = (payload, userId = getEncryptionUserId()) => {
  if (!env.ENCRYPTION_ENABLE) return payload;

  const plain = typeof payload === 'string' ? payload : JSON.stringify(payload);
  const encrypted = encrypt(plain, userId);
  return encrypted ? { body: encrypted } : payload;
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token in storage');
  }

  const refreshPath = getRefreshTokenPath();
  const refreshUrl = resolveRequestUrl(env.API_BASE_URL, refreshPath);
  assertValidRequestUrl({ baseURL: env.API_BASE_URL, url: refreshPath });

  // Bare axios so this request skips the auth interceptors — still encrypt body.
  // Refresh encrypts with SECRET_KEY only (no userId).
  const refreshBody = buildEncryptedBody({ refreshToken }, null);

  const { data: refreshRaw } = await axios.post(refreshUrl, refreshBody, {
    headers: applyApiHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });

  const refreshData = decryptResponseData(refreshRaw, null);

  const {
    accessToken: newToken,
    refreshToken: newRefreshToken,
    expiresIn,
  } = extractAuthTokens(refreshData);

  if (!newToken) {
    throw new Error('Missing access token in refresh response');
  }

  return { newToken, newRefreshToken, expiresIn, refreshToken };
};

const ensureFreshAccessToken = async ({ force = false } = {}) => {
  if (!getRefreshToken()) return null;
  if (!force && !isAccessTokenExpired()) return null;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    isRefreshing = true;
    try {
      const { newToken, newRefreshToken, expiresIn, refreshToken } = await refreshAccessToken();
      store.dispatch(
        setTokens({
          token: newToken,
          refreshToken: newRefreshToken || refreshToken,
          expiresIn,
        }),
      );
      processQueue(null, newToken);
      return newToken;
    } catch (error) {
      processQueue(error, null);
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const retryWithToken = (originalRequest, token) => {
  originalRequest._retry = true;
  originalRequest.headers = originalRequest.headers || {};
  originalRequest._retryToken = token;
  setAuthorizationHeader(originalRequest.headers, token);
  return axiosInstance(originalRequest);
};

export const setupInterceptors = () => {
  if (interceptorsReady) return;
  interceptorsReady = true;

  axiosInstance.interceptors.request.use(
    async (config) => {
      assertValidRequestUrl(config);

      if (!config._retry && !config._retryToken && !shouldSkipTokenRefresh(config.url)) {
        try {
          const refreshedToken = await ensureFreshAccessToken();
          if (refreshedToken) {
            config._retryToken = refreshedToken;
          }
        } catch (refreshError) {
          if (isRefreshSessionExpired(refreshError)) {
            logoutAfterFailedRefresh();
          }
          return Promise.reject(refreshError);
        }
      }

      // APP_TYPE admin → admin token; otherwise → merchant token
      if (config._retryToken) {
        config.headers = setAuthorizationHeader(config.headers || {}, config._retryToken);
      } else {
        config.headers = applyAuthorizationHeader(config.headers || {});
      }
      config.headers = applyApiHeaders(config.headers || {});

      // Let the browser set multipart boundary for FormData uploads.
      if (isFormDataBody(config.data)) {
        if (typeof config.headers?.setContentType === 'function') {
          config.headers.setContentType(false);
        } else if (config.headers) {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }

        if (config.timeout == null || config.timeout === axiosInstance.defaults.timeout) {
          config.timeout = UPLOAD_TIMEOUT_MS;
        }
      }

      encryptRequestData(config);
      logApiRequest(config);

      if (!config.hideLoader) {
        store.dispatch(startLoading(config.loaderKey || 'global'));
      }

      return config;
    },
    (error) => {
      store.dispatch(stopLoading('global'));
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      store.dispatch(stopLoading(response.config?.loaderKey || 'global'));

      if (isApiEncryptionEnabled(response.config)) {
        response.data = decryptResponseData(
          response.data,
          getRequestEncryptionUserId(response.config),
        );
      }

      if (response.config?.showSuccessToast && response.data?.message) {
        toast.success(response.data.message);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      store.dispatch(stopLoading(originalRequest?.loaderKey || 'global'));

      if (error.response && isApiEncryptionEnabled(originalRequest)) {
        error.response.data = decryptResponseData(
          error.response.data,
          getRequestEncryptionUserId(originalRequest),
        );
      }

      if (error?.code === 'ERR_INVALID_URL') {
        if (env.IS_DEV) {
          console.error(error.message);
        }
        if (!originalRequest?.hideErrorToast) {
          toast.error(getApiErrorMessage(error, 'Invalid API request URL.'));
        }
        return Promise.reject(error);
      }

      if (!error.response) {
        if (env.IS_DEV) {
          const attemptedUrl = resolveRequestUrl(
            originalRequest?.baseURL ?? env.API_BASE_URL,
            originalRequest?.url,
          );

          console.error(
            `[API] Network failure for ${attemptedUrl || '(empty URL)'}:`,
            error?.message || error,
          );
        }

        if (!originalRequest?.hideErrorToast) {
          toast.error(
            getApiErrorMessage(error, 'Unable to connect to the server. Please try again.'),
          );
        }
        return Promise.reject(error);
      }

      const { status } = error.response;
      const message = getApiErrorMessage(error);
      const shouldRefreshSession =
        (status === 401 || (status === 403 && isAccessTokenExpired())) &&
        originalRequest &&
        !originalRequest._retry &&
        !shouldSkipTokenRefresh(originalRequest.url);

      // 401 or expired-token 403 → refresh. Refresh failure → login (not /403).
      if (shouldRefreshSession) {
        if (isRefreshing || refreshPromise) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => retryWithToken(originalRequest, token))
            .catch((err) => Promise.reject(err));
        }

        if (!getRefreshToken()) {
          logoutAfterFailedRefresh();
          return Promise.reject(error);
        }

        try {
          const newToken = await ensureFreshAccessToken({ force: true });
          if (!newToken) {
            logoutAfterFailedRefresh();
            return Promise.reject(error);
          }
          return retryWithToken(originalRequest, newToken);
        } catch (refreshError) {
          if (isRefreshSessionExpired(refreshError)) {
            logoutAfterFailedRefresh();
          } else if (!originalRequest?.hideErrorToast) {
            toast.error(
              getApiErrorMessage(refreshError, 'Unable to refresh session. Please try again.'),
            );
          }
          return Promise.reject(refreshError);
        }
      }

      if (status === 403) {
        if (env.APP_TYPE === 'merchant' && shouldHandleMerchantForbiddenRedirect(originalRequest)) {
          redirectMerchantOnForbidden();
        }

        if (!originalRequest?.hideErrorToast) {
          toast.error(message || 'You do not have permission to perform this action.');
        }
      } else if (status === 500) {
        toast.error(message || 'Internal server error. Please try again later.');
      } else if (status === 422 || status === 400) {
        if (!originalRequest?.hideErrorToast) {
          toast.error(message);
        }
      } else if (!originalRequest?.hideErrorToast) {
        toast.error(message);
      }

      return Promise.reject(error);
    },
  );
};

export default setupInterceptors;
